import { useState, type ReactNode } from "react";
import { NavLink, useNavigate } from "react-router";
import {
  Activity,
  CalendarDays,
  Check,
  ChevronsUpDown,
  CircleHelp,
  ClipboardList,
  KeyRound,
  ListChecks,
  LogOut,
  Map,
  Menu,
  MoreHorizontal,
  Moon,
  RefreshCw,
  Repeat2,
  Settings,
  ShieldAlert,
  User,
  UserCircle,
  Webhook,
  Wrench,
  X
} from "lucide-react";
import { useTenant } from "@/lib/tenant-context.js";
import { basePath, setActiveTenantSlug, signOut, useSession } from "@/lib/session.js";
import { Button } from "@/components/ui/button.js";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog.js";
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from "@/components/ui/dropdown.js";
import { Separator } from "@/components/ui/separator.js";
import { Badge } from "@/components/ui/badge.js";
import { useKeyboardShortcuts } from "@/hooks/use-keyboard-shortcuts.js";
import { cn } from "@/lib/cn.js";

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  shortcut?: string;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

export function TenantShell({ children }: { children: ReactNode }): React.JSX.Element {
  const tenant = useTenant();
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const session = useSession();
  const sessionEmail = session.data?.email;
  const sessionRoleLabel = (() => {
    const principal = session.data?.principal;
    if (!principal) return "staff";
    if (principal.kind === "tenant_user") return principal.role.replace("tenant_", "");
    if (principal.kind === "superadmin") return "platform";
    return "viewer";
  })();
  useKeyboardShortcuts({
    "?": () => setHelpOpen(true),
    "g c": () => navigate("/calendar"),
    "g d": () => navigate("/dispatch"),
    "g l": () => navigate("/day-list"),
    "g m": () => navigate("/me"),
    // PRD principal-scoped-reads-and-crm-refs (REQ-PSR-009): the /customers
    // route is still mounted for backstop access but is no longer a primary
    // affordance — keyboard shortcut removed alongside the sidebar entry.
    "g s": () => navigate("/services"),
    "g r": () => navigate("/resources"),
    Escape: () => setHelpOpen(false)
  });

  const sections = navSections();
  const vertical = tenant.me?.tenant.vertical ?? "—";
  const tenantName = tenant.me?.tenant.slug ?? tenant.tenantSlug;

  return (
    <div className="flex min-h-dvh bg-background">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 w-64 shrink-0 border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-transform duration-200 ease-emphasized lg:static lg:translate-x-0",
          navOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b border-sidebar-border px-4 h-14">
            <BrandMark />
            <button
              type="button"
              className="-mr-2 rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground lg:hidden cursor-pointer"
              aria-label="Close navigation"
              onClick={() => setNavOpen(false)}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <TenantSwitcher tenantName={tenantName} vertical={vertical} />

          <nav className="flex-1 overflow-y-auto scrollbar-thin px-2 py-2">
            {sections.map((section) => (
              <div key={section.label} className="mb-1">
                <div className="nav-section-label">{section.label}</div>
                <ul className="flex flex-col gap-0.5">
                  {section.items.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={`/${item.to}`}
                        className={({ isActive }) =>
                          cn(
                            "group flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] font-medium text-muted-foreground transition-colors duration-150 cursor-pointer hover:bg-accent hover:text-accent-foreground",
                            isActive && "bg-accent text-accent-foreground"
                          )
                        }
                        onClick={() => setNavOpen(false)}
                      >
                        {({ isActive }) => (
                          <>
                            <item.icon
                              className={cn(
                                "h-4 w-4 shrink-0 transition-colors",
                                isActive ? "text-primary" : "text-muted-foreground group-hover:text-foreground"
                              )}
                            />
                            <span className="flex-1 truncate">{item.label}</span>
                            {item.shortcut && (
                              <span className="kbd opacity-0 group-hover:opacity-100">{item.shortcut}</span>
                            )}
                          </>
                        )}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          <div className="border-t border-sidebar-border p-2">
            <UserMenu email={sessionEmail ?? "—"} role={sessionRoleLabel} />
          </div>
        </div>
      </aside>

      {/* Backdrop on mobile */}
      {navOpen && (
        <div
          className="fixed inset-0 z-30 bg-foreground/40 backdrop-blur-sm lg:hidden animate-fade-in"
          onClick={() => setNavOpen(false)}
          aria-hidden
        />
      )}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 border-b bg-card/80 backdrop-blur-md px-4 lg:px-6">
          <button
            type="button"
            className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground lg:hidden cursor-pointer"
            aria-label="Toggle navigation"
            onClick={() => setNavOpen((v) => !v)}
          >
            <Menu className="h-4 w-4" />
          </button>

          <div className="hidden items-center gap-2 text-xs text-muted-foreground lg:flex">
            <span className="font-mono uppercase tracking-wider">{tenantName}</span>
            <span aria-hidden>·</span>
            <span className="capitalize">{vertical}</span>
          </div>

          <div className="ml-auto flex items-center gap-1.5">
            <ViewSwitcher />
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Keyboard shortcuts (?)"
              title="Keyboard shortcuts"
              onClick={() => setHelpOpen(true)}
            >
              <CircleHelp className="h-4 w-4" />
            </Button>
          </div>
        </header>

        <main className="flex-1 overflow-auto pb-16 lg:pb-0">
          <div className="page-shell px-4 py-5 lg:px-6 lg:py-6">{children}</div>
        </main>
      </div>

      <MobileTabBar onMore={() => setNavOpen(true)} />
      <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
    </div>
  );
}

/**
 * Bottom-tab bar shown on mobile in place of the sidebar drawer for the
 * most-used routes. The fifth slot ("More") opens the existing sidebar
 * drawer for the long tail (catalog, schedule, system).
 */
function MobileTabBar({ onMore }: { onMore: () => void }): React.JSX.Element {
  const tabs = [
    { to: "/calendar", label: "Calendar", icon: CalendarDays },
    { to: "/day-list", label: "Day list", icon: ClipboardList },
    { to: "/me", label: "Me", icon: User }
  ] as const;
  return (
    <nav
      aria-label="Primary"
      className="fixed inset-x-0 bottom-0 z-30 flex h-14 items-stretch border-t bg-card/95 backdrop-blur-md lg:hidden"
    >
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end
          className={({ isActive }) =>
            cn(
              "flex flex-1 flex-col items-center justify-center gap-0.5 text-[10px] font-medium text-muted-foreground transition-colors duration-100",
              isActive && "text-primary"
            )
          }
        >
          {({ isActive }) => (
            <>
              <tab.icon className={cn("h-5 w-5", isActive && "text-primary")} />
              <span>{tab.label}</span>
            </>
          )}
        </NavLink>
      ))}
      <button
        type="button"
        onClick={onMore}
        aria-label="More"
        className="flex flex-1 flex-col items-center justify-center gap-0.5 text-[10px] font-medium text-muted-foreground transition-colors duration-100 hover:text-foreground"
      >
        <MoreHorizontal className="h-5 w-5" />
        <span>More</span>
      </button>
    </nav>
  );
}

function BrandMark(): React.JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground shadow-sm">
        <CalendarDays className="h-4 w-4" />
      </div>
      <span className="text-sm font-semibold tracking-tight">Calendar</span>
    </div>
  );
}

/**
 * Sidebar tenant identity. For a superadmin (UMS Console admin) this is a live
 * switcher over every tenant in the calendar registry (`session.tenants`, from
 * GET /api/v1/auth/me). Selecting a tenant persists the slug and hard-reloads at
 * the mount root so every tenant-scoped query refetches under the new schema —
 * the x-tenant-slug header now travels on each ApiClient call. For every other
 * principal it's a static label (they're pinned to one tenant).
 */
function TenantSwitcher({ tenantName, vertical }: { tenantName: string; vertical: string }): React.JSX.Element {
  const session = useSession();
  const tenant = useTenant();
  const principal = session.data?.principal;
  const tenants = session.data?.tenants ?? [];
  const canSwitch = principal?.kind === "superadmin" && tenants.length > 0;

  if (!canSwitch) {
    return (
      <div className="border-b border-sidebar-border px-3 py-3">
        <div className="flex items-center justify-between rounded-md border border-sidebar-border bg-card px-2.5 py-2 shadow-xs">
          <div className="flex min-w-0 items-center gap-2">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary-soft text-[11px] font-semibold uppercase text-primary">
              {tenantName.slice(0, 2)}
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold leading-tight text-foreground">{tenantName}</div>
              <div className="text-[11px] capitalize leading-tight text-muted-foreground">{vertical}</div>
            </div>
          </div>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </div>
      </div>
    );
  }

  const activeSlug = tenant.tenantSlug;
  const active = tenants.find((t) => t.slug === activeSlug);
  const activeName = active?.name ?? tenantName;

  const onSelect = (slug: string): void => {
    if (slug === activeSlug) return;
    setActiveTenantSlug(slug);
    window.location.assign(`${basePath()}/`);
  };

  return (
    <div className="border-b border-sidebar-border px-3 py-3">
      <Dropdown>
        <DropdownTrigger className="flex w-full items-center justify-between rounded-md border border-sidebar-border bg-card px-2.5 py-2 text-left shadow-xs transition-colors duration-150 hover:bg-accent cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <div className="flex min-w-0 items-center gap-2">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary-soft text-[11px] font-semibold uppercase text-primary">
              {activeName.slice(0, 2)}
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold leading-tight text-foreground">{activeName}</div>
              <div className="text-[11px] leading-tight text-muted-foreground">Platform · switch tenant</div>
            </div>
          </div>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </DropdownTrigger>
        <DropdownContent align="start" className="max-h-80 w-[15rem] overflow-y-auto">
          <div className="px-2.5 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Tenants ({tenants.length})
          </div>
          {tenants.map((t) => (
            <DropdownItem key={t.slug} onSelect={() => onSelect(t.slug)}>
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-primary-soft text-[10px] font-semibold uppercase text-primary">
                {t.name.slice(0, 2)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm leading-tight text-foreground">{t.name}</div>
                <div className="truncate text-[10px] leading-tight text-muted-foreground">{t.slug}</div>
              </div>
              {t.status !== "active" && (
                <Badge variant="outline" className="capitalize">
                  {t.status}
                </Badge>
              )}
              {t.slug === activeSlug && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
            </DropdownItem>
          ))}
        </DropdownContent>
      </Dropdown>
    </div>
  );
}

function navSections(): NavSection[] {
  return [
    {
      label: "Operations",
      items: [
        { to: "calendar", label: "Calendar", icon: CalendarDays, shortcut: "g c" },
        { to: "dispatch", label: "Dispatch", icon: Map, shortcut: "g d" },
        { to: "day-list", label: "Day list", icon: ClipboardList, shortcut: "g l" },
        { to: "me", label: "Me", icon: User, shortcut: "g m" }
      ]
    },
    {
      label: "Catalog",
      // PRD principal-scoped-reads-and-crm-refs (REQ-PSR-009): customer
      // creation/edit happens in the upstream CRM. The /customers route stays
      // mounted (so deep links keep working) but is no longer surfaced in the
      // sidebar nav at any breakpoint.
      items: [
        { to: "services", label: "Services", icon: Wrench, shortcut: "g s" },
        { to: "resources", label: "Resources", icon: UserCircle, shortcut: "g r" },
        { to: "resource-skills", label: "Skill matrix", icon: KeyRound }
      ]
    },
    {
      label: "Schedule",
      items: [
        { to: "schedules", label: "Schedules", icon: Repeat2 },
        { to: "recurrences", label: "Recurrences", icon: Repeat2 },
        { to: "closures", label: "Closures", icon: Moon }
      ]
    },
    {
      label: "Sync",
      items: [
        { to: "calendar-sync", label: "Calendar sync", icon: RefreshCw },
        { to: "sync-conflicts", label: "Sync conflicts", icon: ShieldAlert },
        { to: "webhooks", label: "Webhooks", icon: Webhook }
      ]
    },
    {
      label: "System",
      items: [
        { to: "audit-log", label: "Audit log", icon: ListChecks },
        { to: "dlq", label: "Dead letter queue", icon: Activity },
        { to: "settings", label: "Settings", icon: Settings }
      ]
    }
  ];
}

function ViewSwitcher(): React.JSX.Element {
  const tenant = useTenant();
  const navigate = useNavigate();
  return (
    <Dropdown>
      <DropdownTrigger
        className={cn(
          "inline-flex h-8 items-center gap-1.5 rounded-md border border-border-strong bg-card px-2.5 text-xs font-medium text-foreground shadow-xs transition-colors duration-150 hover:bg-accent hover:text-accent-foreground cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        )}
      >
        <CalendarDays className="h-3.5 w-3.5" />
        <span>View</span>
        <ChevronsUpDown className="h-3 w-3 text-muted-foreground" />
      </DropdownTrigger>
      <DropdownContent>
        <DropdownItem onSelect={() => navigate("/calendar")}>
          <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
          Calendar grid
        </DropdownItem>
        <DropdownItem onSelect={() => navigate("/dispatch")}>
          <Map className="h-3.5 w-3.5 text-muted-foreground" />
          Dispatch map
        </DropdownItem>
        <DropdownItem onSelect={() => navigate("/day-list")}>
          <ClipboardList className="h-3.5 w-3.5 text-muted-foreground" />
          Day list
        </DropdownItem>
      </DropdownContent>
    </Dropdown>
  );
}

function UserMenu({ email, role }: { email: string; role: string }): React.JSX.Element {
  const navigate = useNavigate();
  const initials = email.slice(0, 2).toUpperCase();
  return (
    <Dropdown>
      <DropdownTrigger
        className={cn(
          "flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm transition-colors duration-150 hover:bg-accent hover:text-accent-foreground cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        )}
      >
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-[10px] font-semibold uppercase text-primary-foreground">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-medium text-foreground">{email}</div>
          <div className="truncate text-[10px] capitalize text-muted-foreground">{role}</div>
        </div>
        <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      </DropdownTrigger>
      <DropdownContent align="start">
        <div className="px-2.5 py-1.5">
          <div className="text-xs font-medium text-foreground">{email}</div>
          <Badge variant="outline" className="mt-1 capitalize">
            {role}
          </Badge>
        </div>
        <Separator />
        <DropdownItem onSelect={() => navigate("#account")}>
          <UserCircle className="h-3.5 w-3.5 text-muted-foreground" />
          Account
        </DropdownItem>
        <DropdownItem onSelect={() => navigate("#api-keys")}>
          <KeyRound className="h-3.5 w-3.5 text-muted-foreground" />
          API keys
        </DropdownItem>
        <Separator />
        <DropdownItem
          onSelect={() => {
            void signOut().catch(()=>window.alert("Could not sign out. Please try again."));
          }}
        >
          <LogOut className="h-3.5 w-3.5 text-muted-foreground" />
          Sign out
        </DropdownItem>
      </DropdownContent>
    </Dropdown>
  );
}

function HelpDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }): React.JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>Linear-style. Press anywhere outside an input.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <ShortcutGroup
            label="Actions"
            items={[
              { k: "c", label: "Create (context)" },
              { k: "e", label: "Edit selected" },
              { k: "j", label: "Next (day / row)" },
              { k: "k", label: "Previous (day / row)" },
              { k: "?", label: "Show this help" }
            ]}
          />
          <ShortcutGroup
            label="Navigate"
            items={[
              { k: "g c", label: "Calendar" },
              { k: "g d", label: "Dispatch" },
              { k: "g l", label: "Day list" },
              { k: "g s", label: "Services" },
              { k: "g r", label: "Resources" }
            ]}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ShortcutGroup({ label, items }: { label: string; items: Array<{ k: string; label: string }> }): React.JSX.Element {
  return (
    <div>
      <div className="nav-section-label !pt-0">{label}</div>
      <ul className="flex flex-col gap-0.5">
        {items.map((item) => (
          <li key={item.k} className="flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-sm hover:bg-muted">
            <span className="text-muted-foreground">{item.label}</span>
            <span className="kbd">{item.k}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
